import { useMemo } from "react";

/**
 * Compact atom / orbital visual — decorative filler for empty sections.
 * Cheaper than the full KnowledgeCore. Configurable size + accent.
 */
export default function MiniKnowledgeCore({ size = 200, accent = "cyan" }) {
  const colors = {
    cyan:   { core: "#67E8F9", orbit: "#22D3EE", glow: "rgba(103,232,249,0.5)" },
    indigo: { core: "#818CF8", orbit: "#6366F1", glow: "rgba(129,140,248,0.5)" },
    purple: { core: "#C084FC", orbit: "#A855F7", glow: "rgba(192,132,252,0.5)" },
  }[accent] || { core: "#67E8F9", orbit: "#22D3EE", glow: "rgba(103,232,249,0.5)" };

  const atomlets = useMemo(() => (
    Array.from({ length: 4 }).map((_, i) => ({
      id: `mkc-${i}`,
      angle: (i / 4) * 360,
      radius: size * 0.38,
      duration: 10 + i * 2,
    }))
  ), [size]);

  return (
    <div className="relative pointer-events-none" style={{ width: size, height: size }}>
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Orbital rings */}
        {[0, 60, 120].map((rot, i) => (
          <div
            key={rot}
            className="absolute inset-0 flex items-center justify-center"
            style={{ transform: `rotate(${rot}deg)` }}
          >
            <div
              className="rounded-full border"
              style={{
                width: size * (0.55 + i * 0.12),
                height: size * (0.55 + i * 0.12) * 0.42,
                borderColor: `${colors.orbit}55`,
              }}
            />
            <div
              className="absolute w-2 h-2 rounded-full"
              style={{
                background: colors.core,
                boxShadow: `0 0 12px ${colors.core}, 0 0 24px ${colors.core}`,
                animation: `mkc-orbit-${i} ${11 + i * 2}s linear infinite`,
              }}
            />
          </div>
        ))}
        {/* Central nucleus */}
        <div
          className="rounded-full"
          style={{
            width: size * 0.22,
            height: size * 0.22,
            background: `radial-gradient(circle at 30% 30%, #A5F3FC, ${colors.orbit} 55%, #4F46E5)`,
            boxShadow: `0 0 30px ${colors.glow}, 0 0 60px ${colors.glow}, inset 0 0 15px rgba(255,255,255,0.35)`,
            animation: "mkc-pulse 3.5s ease-in-out infinite",
          }}
        />
      </div>

      {/* Outer atomlets */}
      {atomlets.map((a, i) => (
        <div
          key={a.id}
          className="absolute top-1/2 left-1/2"
          style={{
            width: 5, height: 5, marginLeft: -2.5, marginTop: -2.5,
            animation: `mkc-atomlet-${i} ${a.duration}s linear infinite`,
          }}
        >
          <div
            className="w-full h-full rounded-full"
            style={{ background: colors.core, boxShadow: `0 0 10px ${colors.core}` }}
          />
        </div>
      ))}

      <style>{`
        @keyframes mkc-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes mkc-orbit-0 {
          0% { transform: translate(${size * 0.275}px, 0) }
          25% { transform: translate(0, -${size * 0.115}px) }
          50% { transform: translate(-${size * 0.275}px, 0) }
          75% { transform: translate(0, ${size * 0.115}px) }
          100% { transform: translate(${size * 0.275}px, 0) }
        }
        @keyframes mkc-orbit-1 {
          0% { transform: translate(${size * 0.335}px, 0) }
          25% { transform: translate(0, -${size * 0.14}px) }
          50% { transform: translate(-${size * 0.335}px, 0) }
          75% { transform: translate(0, ${size * 0.14}px) }
          100% { transform: translate(${size * 0.335}px, 0) }
        }
        @keyframes mkc-orbit-2 {
          0% { transform: translate(${size * 0.395}px, 0) }
          25% { transform: translate(0, -${size * 0.165}px) }
          50% { transform: translate(-${size * 0.395}px, 0) }
          75% { transform: translate(0, ${size * 0.165}px) }
          100% { transform: translate(${size * 0.395}px, 0) }
        }
        ${Array.from({ length: 4 }).map((_, i) => {
          const r = size * 0.38;
          const a0 = (i / 4) * 360;
          return `
            @keyframes mkc-atomlet-${i} {
              from { transform: rotate(${a0}deg) translateX(${r}px) rotate(-${a0}deg); }
              to   { transform: rotate(${a0 + 360}deg) translateX(${r}px) rotate(-${a0 + 360}deg); }
            }
          `;
        }).join("\n")}
      `}</style>
    </div>
  );
}
